import React, { useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItemCheckbox,
	DropdownItemCheckboxGroup,
} from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useQueryParams } from '@atlassian/jira-software-router-utils/src/services/query-params/index.tsx';
import type { Status } from '@atlassian/jira-version-modals/src/common/model/index.tsx';
import {
	ARCHIVED,
	RELEASED,
	UNRELEASED,
} from '@atlassian/jira-version-modals/src/common/model/status.tsx';
import { QUERY_PARAM_STATUS } from '../../common/constants.tsx';
import {
	getUpdatedDisplayedStatuses,
	transformStatusFromParam,
	transformStatusToDropdownSelection,
	transformStatusToParam,
} from '../../common/utils.tsx';
import messages from './messages.tsx';

type StatusDropdownItemType = {
	id: string;
	value: Status;
	label: { id: string; defaultMessage: string; description: string };
};

const statusDropdownItems: StatusDropdownItemType[] = [
	{ id: 'released', value: RELEASED, label: messages.released },
	{ id: 'unreleased', value: UNRELEASED, label: messages.unreleased },
	{ id: 'archived', value: ARCHIVED, label: messages.archived },
];

const StatusFilter = () => {
	const [queryParams, setQueryParams] = useQueryParams(QUERY_PARAM_STATUS);
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Displayed statuses
	const statuses = transformStatusFromParam(queryParams[QUERY_PARAM_STATUS]);
	// Selected statuses (in the dropdown)
	const [selectedStatuses, setSelectedStatuses] = useState<Status[]>(statuses);

	useEffect(() => {
		setSelectedStatuses(transformStatusToDropdownSelection(queryParams[QUERY_PARAM_STATUS]));
	}, [queryParams]);

	const handleFilterSelection = debounce((selectedItem: Status) => {
		const updatedDisplayedStatuses = getUpdatedDisplayedStatuses(
			statuses,
			selectedStatuses,
			selectedItem,
		);
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'itemClicked',
				actionSubject: 'filterDropdown',
			}),
			'statusFilterFilterDropdown',
			{
				type: selectedItem,
			},
		);
		setQueryParams({
			[QUERY_PARAM_STATUS]: transformStatusToParam(updatedDisplayedStatuses),
		});
	}, 300);

	const getStatusFilterDropdownLabel = () => {
		if (selectedStatuses.length === 0 || selectedStatuses.length === statusDropdownItems.length) {
			return formatMessage(messages.all);
		}

		const labels = statusDropdownItems
			.filter((item) => selectedStatuses.includes(item.value))
			.map((item) => formatMessage(item.label));
		return labels.join(', ');
	};

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					// TODO: This ID might be used in polli tests so preserving it until we update them.
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="project-directories.versions.main.filter.versions-filter.filter-dropdown.button"
					iconAfter={<ChevronDownIcon label="" />}
					ref={triggerRef}
				>
					{getStatusFilterDropdownLabel()}
				</Button>
			)}
		>
			<DropdownItemCheckboxGroup id="actions">
				{statusDropdownItems.map((status: StatusDropdownItemType) => (
					<DropdownItemCheckbox
						id={status.id}
						onClick={() => handleFilterSelection(status.value)}
						isSelected={selectedStatuses.includes(status.value)}
						key={status.value}
					>
						{formatMessage(status.label)}
					</DropdownItemCheckbox>
				))}
			</DropdownItemCheckboxGroup>
		</DropdownMenu>
	);
};
export default StatusFilter;
